import { watch, onScopeDispose } from 'vue';
import type { Ref } from 'vue';

/**
 * Trava o scroll do body enquanto qualquer modal/painel estiver aberto.
 *
 * Contador de referência em nível de módulo: vários modais podem estar
 * "ativos" ao mesmo tempo (ex.: o Giratudo reabre logo após o depósito
 * fechar), então quem controla o overflow é a contagem — nunca um modal
 * isolado. Só volta a rolar quando o último fecha.
 *
 * Mantém o padrão já validado do layout (mesma escolha do menu mobile):
 * aplica `overflow: hidden` e no fim usa `removeProperty('overflow')` em vez
 * de forçar `scroll`. Com `scroll` (+ `height: 100%`) quem rola passa a ser o
 * body, e o Safari iOS só esconde a barra de endereço quando quem rola é a
 * janela/documento — remover a propriedade devolve o scroll pra window.
 */
let lockCount = 0;

function apply() {
  if (!import.meta.client) {
    return;
  }

  if (lockCount > 0) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.removeProperty('overflow');
  }
}

function acquire() {
  lockCount++;
  apply();
}

function release() {
  if (lockCount === 0) {
    return;
  }

  lockCount--;
  apply();
}

/**
 * `true` enquanto qualquer modal/painel mantiver o scroll travado. Leitura
 * síncrona do contador — pensada pra handlers não-reativos (ex.: o gesto do
 * pull-to-refresh) que precisam saber, no momento do toque, se há um modal
 * aberto sem depender da classe legada `modal-open`.
 */
export function isBodyScrollLocked(): boolean {
  return lockCount > 0;
}

/**
 * Observa um booleano reativo e trava/destrava o body conforme ele abre e
 * fecha. Libera sozinho ao desmontar o componente (evita lock preso se o
 * modal sumir sem transicionar `false`).
 */
export function useBodyScrollLock(source: Ref<boolean>) {
  let held = false;

  function set(active: boolean) {
    if (active && !held) {
      held = true;
      acquire();
    } else if (!active && held) {
      held = false;
      release();
    }
  }

  watch(source, set, { immediate: true });

  onScopeDispose(() => set(false));
}
